import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import apiClient from "../services/api";

const AdminEnquiriesPage = () => {
  const navigate = useNavigate();
  const [intakeEnquiries, setIntakeEnquiries] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [volunteerApps, setVolunteerApps] = useState([]);
  const [rehomingApps, setRehomingApps] = useState([]);
  const [welfareReports, setWelfareReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    try {
      const [intake, contact, volunteer, rehoming, welfare] = await Promise.all([
        apiClient.get("/intake-enquiries"),
        apiClient.get("/contact"),
        apiClient.get("/volunteer-applications"),
        apiClient.get("/rehoming-applications"),
        apiClient.get("/welfare-reports")
      ]);

      setIntakeEnquiries(intake.data);
      setContactMessages(contact.data);
      setVolunteerApps(volunteer.data);
      setRehomingApps(rehoming.data);
      setWelfareReports(welfare.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const colors = {
      new: "bg-blue-100 text-blue-800",
      in_progress: "bg-yellow-100 text-yellow-800",
      resolved: "bg-green-100 text-green-800",
      closed: "bg-gray-100 text-gray-800"
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || colors.new}`}>
        {status.replace("_", " ").toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b bg-white">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">Form Submissions</h1>
          <Button variant="outline" onClick={() => navigate("/admin/dashboard")}>
            Back to Dashboard
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Tabs defaultValue="intake" className="space-y-6">
          <TabsList>
            <TabsTrigger value="intake">
              Intake Enquiries ({intakeEnquiries.length})
            </TabsTrigger>
            <TabsTrigger value="contact">
              Contact Messages ({contactMessages.length})
            </TabsTrigger>
            <TabsTrigger value="volunteer">
              Volunteers ({volunteerApps.length})
            </TabsTrigger>
            <TabsTrigger value="rehoming">
              Rehoming ({rehomingApps.length})
            </TabsTrigger>
            <TabsTrigger value="welfare">
              Welfare Reports ({welfareReports.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="intake" className="space-y-4">
            {intakeEnquiries.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No intake enquiries yet.
                </CardContent>
              </Card>
            ) : (
              intakeEnquiries.map((enquiry) => (
                <Card key={enquiry.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle>{enquiry.horse_name}</CardTitle>
                      <StatusBadge status={enquiry.status} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Owner</p>
                        <p className="font-medium">{enquiry.owner_name}</p>
                        <p className="text-sm text-gray-600">{enquiry.email}</p>
                        {enquiry.phone && <p className="text-sm text-gray-600">{enquiry.phone}</p>}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Horse Details</p>
                        <p className="text-sm">Age: {enquiry.horse_age || "N/A"}</p>
                        <p className="text-sm">Breed: {enquiry.horse_breed || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Reason</p>
                        <p className="text-sm capitalize">{enquiry.reason.replace("_", " ")}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Urgency</p>
                        <p className="text-sm capitalize">{enquiry.urgency || "N/A"}</p>
                      </div>
                    </div>
                    {enquiry.additional_info && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm text-gray-500">Additional Information</p>
                        <p className="text-sm mt-1">{enquiry.additional_info}</p>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-4">
                      Submitted: {new Date(enquiry.created_at).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="contact" className="space-y-4">
            {contactMessages.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No contact messages yet.
                </CardContent>
              </Card>
            ) : (
              contactMessages.map((message) => (
                <Card key={message.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle>{message.name}</CardTitle>
                      <StatusBadge status={message.status} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-gray-500">Contact</p>
                        <p className="text-sm">{message.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Subject</p>
                        <p className="text-sm capitalize">{message.subject.replace("_", " ")}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Message</p>
                        <p className="text-sm mt-1">{message.message}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-4">
                        Submitted: {new Date(message.created_at).toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="volunteer" className="space-y-4">
            {volunteerApps.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No volunteer applications yet.
                </CardContent>
              </Card>
            ) : (
              volunteerApps.map((app) => (
                <Card key={app.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle>{app.name}</CardTitle>
                      <StatusBadge status={app.status} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-gray-500">Contact</p>
                        <p className="text-sm">{app.email}</p>
                        {app.phone && <p className="text-sm">{app.phone}</p>}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Availability</p>
                        <p className="text-sm capitalize">{app.availability || "N/A"}</p>
                      </div>
                      {app.interests && (
                        <div>
                          <p className="text-sm text-gray-500">Interests</p>
                          <p className="text-sm">{app.interests}</p>
                        </div>
                      )}
                      {app.experience && (
                        <div>
                          <p className="text-sm text-gray-500">Experience</p>
                          <p className="text-sm">{app.experience}</p>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-4">
                        Submitted: {new Date(app.created_at).toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="rehoming" className="space-y-4">
            {rehomingApps.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No rehoming applications yet.
                </CardContent>
              </Card>
            ) : (
              rehomingApps.map((app) => (
                <Card key={app.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle>{app.applicant_name}</CardTitle>
                      <StatusBadge status={app.status} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Contact</p>
                        <p className="text-sm">{app.email}</p>
                        <p className="text-sm">{app.phone}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Address</p>
                        <p className="text-sm">{app.address}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Property Type</p>
                        <p className="text-sm">{app.property_type}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Experience Level</p>
                        <p className="text-sm capitalize">{app.experience_level}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Current Horses</p>
                        <p className="text-sm">{app.current_horses}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t space-y-2">
                      <div>
                        <p className="text-sm text-gray-500">Facilities</p>
                        <p className="text-sm mt-1">{app.facilities_description}</p>
                      </div>
                      {app.horse_interest && (
                        <div>
                          <p className="text-sm text-gray-500">Horse Interest</p>
                          <p className="text-sm">{app.horse_interest}</p>
                        </div>
                      )}
                      {app.additional_info && (
                        <div>
                          <p className="text-sm text-gray-500">Additional Info</p>
                          <p className="text-sm">{app.additional_info}</p>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-4">
                      Submitted: {new Date(app.created_at).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="welfare" className="space-y-4">
            {welfareReports.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No welfare reports yet.
                </CardContent>
              </Card>
            ) : (
              welfareReports.map((report) => (
                <Card key={report.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle>{report.yard_name}</CardTitle>
                      <StatusBadge status={report.status} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-gray-500">Location</p>
                        <p className="text-sm">{report.location}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Concern Type</p>
                        <p className="text-sm capitalize">{report.concern_type.replace("_", " ")}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Description</p>
                        <p className="text-sm mt-1">{report.description}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Reporter</p>
                        <p className="text-sm">
                          {report.anonymous ? "Anonymous" : report.contact_email || "N/A"}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 mt-4">
                        Submitted: {new Date(report.created_at).toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminEnquiriesPage;
